"use client";

import { useEffect, useRef, useState } from "react";

/**
 * SearchableSelect —— 可搜索下拉组件
 *
 * Props:
 *   value           当前选中的 option.value
 *   onChange(value) 选中回调
 *   options         [{ value, label }] 数组
 *   placeholder     触发按钮 placeholder（未选中时显示）
 *   emptyText       搜索无结果时显示的文字
 *   className       可选外层类名
 *   disabled        是否禁用
 *
 * 用法示例：
 *   <SearchableSelect
 *       value={form.brand}
 *       onChange={(v) => handleChange('brand', v)}
 *       options={brands.map(b => ({ value: b.id, label: getLocalizedField(b, "name", lang) }))}
 *       placeholder={t("publish.selectBrand")}
 *   />
 */
export default function SearchableSelect({
    value,
    onChange,
    options,
    placeholder = "Sélectionner...",
    emptyText = "Aucun résultat",
    className = "",
    disabled = false,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIdx, setActiveIdx] = useState(-1);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // 当前选中项的 label
    const selectedLabel = options.find((o) => String(o.value) === String(value))?.label || "";

    // 按 query 过滤
    const filtered = (query || "").trim() === ""
        ? options
        : options.filter((o) =>
            o.label.toLowerCase().includes(query.trim().toLowerCase())
        );

    // 打开下拉时聚焦搜索框
    useEffect(() => {
        if (open) {
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 10);
        } else {
            setQuery("");
            setActiveIdx(-1);
        }
    }, [open]);

    // 点击外部关闭
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    // 键盘导航
    const handleKeyDown = (e) => {
        if (!open) {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                e.preventDefault();
                setOpen(true);
            }
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIdx >= 0 && filtered[activeIdx]) {
                onChange(filtered[activeIdx].value);
                setOpen(false);
            }
        } else if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
        }
    };

    // 滚动激活项到可视区域
    useEffect(() => {
        if (activeIdx < 0 || !listRef.current) return;
        const item = listRef.current.children[activeIdx];
        item?.scrollIntoView({ block: "nearest" });
    }, [activeIdx]);

    // query 变化时重置 activeIdx
    useEffect(() => {
        setActiveIdx(filtered.length > 0 ? 0 : -1);
    }, [query]);

    return (
        <div ref={wrapperRef} className={"searchable-select " + className} style={{ position: "relative", width: "100%" }}>
            {/* 触发按钮 */}
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((o) => !o)}
                onKeyDown={handleKeyDown}
                aria-haspopup="listbox"
                aria-expanded={open}
                style={{
                    width: "100%",
                    height: 42,
                    padding: "0 12px",
                    border: "1.5px solid var(--color-border)",
                    borderRadius: 10,
                    background: "var(--color-surface)",
                    fontSize: 14,
                    cursor: disabled ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    color: selectedLabel ? "var(--color-text)" : "var(--color-text-muted)",
                    opacity: disabled ? 0.6 : 1,
                    transition: "border-color 180ms",
                    outline: "none",
                    textAlign: "left",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-brand)")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {selectedLabel || placeholder}
                </span>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ marginLeft: 8, flexShrink: 0, transition: "transform 200ms", transform: open ? "rotate(180deg)" : "rotate(0)", color: "var(--color-text-muted)" }}>
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {/* 下拉面板 */}
            {open && (
                <div
                    role="listbox"
                    style={{
                        position: "absolute",
                        top: "calc(100% + 6px)",
                        left: 0,
                        right: 0,
                        zIndex: 50,
                        background: "var(--color-surface, white)",
                        border: "1.5px solid var(--color-border)",
                        borderRadius: 10,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        overflow: "hidden",
                        maxHeight: 320,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* 搜索输入框 */}
                    <div style={{ padding: 8, borderBottom: "1px solid var(--color-border)" }}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Rechercher..."
                            onKeyDown={(e) => {
                                if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
                                if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
                                if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (activeIdx >= 0 && filtered[activeIdx]) {
                                        onChange(filtered[activeIdx].value);
                                        setOpen(false);
                                    }
                                }
                            }}
                            style={{
                                width: "100%",
                                height: 32,
                                padding: "0 10px",
                                border: "1px solid var(--color-border)",
                                borderRadius: 6,
                                fontSize: 13,
                                outline: "none",
                                background: "transparent",
                            }}
                        />
                    </div>

                    {/* 选项列表 */}
                    <div ref={listRef} style={{ overflowY: "auto", flex: 1 }}>
                        {filtered.length === 0 ? (
                            <div style={{ padding: "16px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>
                                {emptyText}
                            </div>
                        ) : (
                            filtered.map((opt, idx) => {
                                const isSelected = String(opt.value) === String(value);
                                const isActive = idx === activeIdx;
                                return (
                                    <div
                                        key={String(opt.value)}
                                        role="option"
                                        aria-selected={isSelected}
                                        onMouseEnter={() => setActiveIdx(idx)}
                                        onClick={() => {
                                            onChange(opt.value);
                                            setOpen(false);
                                        }}
                                        style={{
                                            padding: "8px 12px",
                                            fontSize: 14,
                                            cursor: "pointer",
                                            background: isActive ? "var(--color-brand-lighter, rgba(83,201,6,0.08))" : "transparent",
                                            color: isSelected ? "var(--color-brand)" : "var(--color-text)",
                                            fontWeight: isSelected ? 600 : 400,
                                            borderLeft: isSelected ? "3px solid var(--color-brand)" : "3px solid transparent",
                                        }}
                                    >
                                        {opt.label}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
